import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';

// Material UI
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';

// Services
import * as warehouseService from '../services/warehouseService';
import * as partService from '../services/partService';
import * as warehouseReorderService from '../services/warehouseReorderService';

// Validation schema
const validationSchema = Yup.object({
  warehouse_id: Yup.number().required('Warehouse is required'),
  part_id: Yup.number().required('Part is required'),
  reorder_point: Yup.number()
    .required('Reorder point is required')
    .integer('Must be an integer')
    .min(0, 'Must be at least 0'),
  target_stock_level: Yup.number()
    .nullable()
    .integer('Must be an integer')
    .min(0, 'Must be at least 0')
    .test(
      'greater-than-reorder',
      'Must be greater than reorder point',
      function (value) {
        return !value || value > this.parent.reorder_point;
      }
    )
});

const WarehouseReorderForm = ({ reorderId = null, onSuccess }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [warehouses, setWarehouses] = useState([]);
  const [parts, setParts] = useState([]);
  const [initialValues, setInitialValues] = useState({
    warehouse_id: '',
    part_id: '',
    reorder_point: 0,
    target_stock_level: ''
  });

  // Fetch warehouses and parts on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch warehouses
        const warehousesData = await warehouseService.getWarehouses();
        setWarehouses(warehousesData);
        
        // Fetch parts (only stock parts)
        const partsData = await partService.getParts({ type: 'stock' });
        setParts(partsData);
        
        // If editing, fetch existing reorder point
        if (reorderId) {
          const reorderData = await warehouseReorderService.getWarehouseReorderById(reorderId);
          setInitialValues({
            warehouse_id: reorderData.warehouse_id,
            part_id: reorderData.part_id,
            reorder_point: reorderData.reorder_point,
            target_stock_level: reorderData.target_stock_level || ''
          });
        }
        
        setLoading(false);
      } catch (error) {
        console.error('Error fetching data:', error);
        toast.error('Failed to load data');
        setLoading(false);
      }
    };
    
    fetchData();
  }, [reorderId]);

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      setSubmitting(true);
      
      // Clean up values
      const submitData = {
        ...values,
        target_stock_level: values.target_stock_level || null
      };
      
      if (reorderId) {
        // Update existing reorder point
        await warehouseReorderService.updateWarehouseReorder(reorderId, submitData);
        toast.success('Warehouse reorder point updated successfully');
      } else {
        // Create new reorder point
        await warehouseReorderService.createWarehouseReorder(submitData);
        toast.success('Warehouse reorder point created successfully');
      }
      
      setSubmitting(false);
      
      // Call success callback if provided
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      console.error('Error saving warehouse reorder point:', error);
      toast.error(error.message || 'Failed to save warehouse reorder point');
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Card>
      <CardHeader
        title={reorderId ? 'Edit Warehouse Reorder Point' : 'Add Warehouse Reorder Point'}
        subheader="Set warehouse-specific reorder points for parts"
      />
      <CardContent>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            isSubmitting
          }) => (
            <Form>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <FormControl
                    fullWidth
                    error={touched.warehouse_id && Boolean(errors.warehouse_id)}
                  >
                    <InputLabel id="warehouse-label">Warehouse</InputLabel>
                    <Field
                      as={Select}
                      name="warehouse_id"
                      labelId="warehouse-label"
                      label="Warehouse"
                      value={values.warehouse_id}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      disabled={isSubmitting || Boolean(reorderId)}
                    >
                      {warehouses.map((warehouse) => (
                        <MenuItem key={warehouse.id} value={warehouse.id}>
                          {warehouse.name}
                        </MenuItem>
                      ))}
                    </Field>
                    {touched.warehouse_id && errors.warehouse_id && (
                      <FormHelperText>{errors.warehouse_id}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <FormControl
                    fullWidth
                    error={touched.part_id && Boolean(errors.part_id)}
                  >
                    <InputLabel id="part-label">Part</InputLabel>
                    <Field
                      as={Select}
                      name="part_id"
                      labelId="part-label"
                      label="Part"
                      value={values.part_id}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      disabled={isSubmitting || Boolean(reorderId)}
                    >
                      {parts.map((part) => (
                        <MenuItem key={part.id} value={part.id}>
                          {part.internal_part_number} - {part.description}
                        </MenuItem>
                      ))}
                    </Field>
                    {touched.part_id && errors.part_id && (
                      <FormHelperText>{errors.part_id}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Field
                    as={TextField}
                    fullWidth
                    name="reorder_point"
                    label="Reorder Point"
                    type="number"
                    value={values.reorder_point}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.reorder_point && Boolean(errors.reorder_point)}
                    helperText={touched.reorder_point && errors.reorder_point}
                    disabled={isSubmitting}
                    InputProps={{ inputProps: { min: 0 } }}
                  />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Field
                    as={TextField}
                    fullWidth
                    name="target_stock_level"
                    label="Target Stock Level (Optional)"
                    type="number"
                    value={values.target_stock_level}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.target_stock_level && Boolean(errors.target_stock_level)}
                    helperText={touched.target_stock_level && errors.target_stock_level}
                    disabled={isSubmitting}
                    InputProps={{ inputProps: { min: 0 } }}
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
                    <Button
                      variant="outlined"
                      onClick={() => onSuccess ? onSuccess() : navigate(-1)}
                      disabled={isSubmitting}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <CircularProgress size={24} />
                      ) : reorderId ? (
                        'Update'
                      ) : (
                        'Create'
                      )}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Form>
          )}
        </Formik>
      </CardContent>
    </Card>
  );
};

export default WarehouseReorderForm;
